import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import { Toaster } from 'sonner'
import { AuthProvider } from './providers/AuthProvider'
import ProtectedRoute from './components/ProtectedRoute'
import RequireRole from './components/RequireRole'
import { PageLoader } from './components/ui'
import './App.css'

// Lazy load AdminLayout - only needed for dashboard routes
const AdminLayout = lazy(() =>
  import('./components/admin').then((m) => ({ default: m.AdminLayout })),
)

// Lazy load InviteLinkHandler - only active when invite code in URL
const InviteLinkHandler = lazy(() =>
  import('./components/InviteLinkHandler').then((m) => ({ default: m.InviteLinkHandler })),
)

// Redirect component for legacy RSVP routes
function RsvpRedirect() {
  const { code } = useParams<{ code: string }>()
  return <Navigate to={`/invite/${code}`} replace />
}

// Lazy-loaded pages for code splitting
const HomePage = lazy(() => import('./pages/HomePage'))
const LoginPage = lazy(() => import('./pages/LoginPage'))
const OurStoryPage = lazy(() => import('./pages/OurStoryPage'))
const InviteLandingPage = lazy(() => import('./pages/InviteLandingPage'))
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'))
const GuestManagementPage = lazy(() =>
  import('./pages/GuestManagementPage').then((m) => ({ default: m.GuestManagementPage })),
)
const DriverManagementPage = lazy(() =>
  import('./pages/DriverManagementPage').then((m) => ({ default: m.DriverManagementPage })),
)
const TripManagementPage = lazy(() =>
  import('./pages/TripManagementPage').then((m) => ({ default: m.TripManagementPage })),
)
// RsvpDashboardPage consolidated into GuestManagementPage
const EventRsvpDashboardPage = lazy(() =>
  import('./pages/EventRsvpDashboardPage').then((m) => ({ default: m.EventRsvpDashboardPage })),
)
const EventManagementPage = lazy(() =>
  import('./pages/EventManagementPage').then((m) => ({ default: m.EventManagementPage })),
)
const AdminUsersPage = lazy(() => import('./pages/AdminUsersPage'))
const AccommodationPage = lazy(() => import('./pages/AccommodationPage'))
const FoodPlanningPage = lazy(() =>
  import('./pages/FoodPlanningPage').then((m) => ({ default: m.FoodPlanningPage })),
)
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'))
const AdminDataMaintenancePage = lazy(() =>
  import('./pages/AdminDataMaintenancePage').then((m) => ({ default: m.AdminDataMaintenancePage })),
)

// Lazy-loaded components
const QRScanner = lazy(() =>
  import('./components/QRScanner').then((m) => ({ default: m.QRScanner })),
)
const CheckInDashboard = lazy(() =>
  import('./components/CheckInDashboard').then((m) => ({ default: m.CheckInDashboard })),
)

// Conditionally render InviteLinkHandler only for invite routes
function ConditionalInviteLinkHandler() {
  const location = useLocation()
  const isInviteRoute =
    location.pathname.startsWith('/invite/') || location.pathname.startsWith('/rsvp/')

  if (!isInviteRoute) return null

  return (
    <Suspense fallback={null}>
      <InviteLinkHandler />
    </Suspense>
  )
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <ConditionalInviteLinkHandler />
        <Suspense fallback={<PageLoader variant="fullPage" />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/story" element={<OurStoryPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/invite/:inviteId" element={<InviteLandingPage />} />
            {/* Redirect old RSVP route to invite page (RSVP form is now in InviteLandingPage) */}
            <Route path="/rsvp/:code" element={<RsvpRedirect />} />
            <Route path="/checkin/:code" element={<QRScanner />} />

            {/* Protected Dashboard Routes - Admin/Planner Only */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <RequireRole role={['admin', 'planner']}>
                    <AdminLayout />
                  </RequireRole>
                </ProtectedRoute>
              }
            >
              {/* Admin Dashboard Home */}
              <Route index element={<AdminDashboard />} />

              {/* Guest Management */}
              <Route path="guests" element={<GuestManagementPage />} />

              {/* Driver Management */}
              <Route path="drivers" element={<DriverManagementPage />} />

              {/* Trip Management */}
              <Route path="trips" element={<TripManagementPage />} />

              {/* RSVP Dashboard - redirect to Guest Management (consolidated) */}
              <Route path="rsvp" element={<Navigate to="/dashboard/guests" replace />} />

              {/* Event RSVP Dashboard */}
              <Route path="event-rsvp" element={<EventRsvpDashboardPage />} />

              {/* Event Management - Admin Only */}
              <Route
                path="events"
                element={
                  <RequireRole role={['admin']}>
                    <EventManagementPage />
                  </RequireRole>
                }
              />

              {/* User Management - Admin Only */}
              <Route
                path="admin/users"
                element={
                  <RequireRole role={['admin']}>
                    <AdminUsersPage />
                  </RequireRole>
                }
              />

              {/* Check-in Dashboard */}
              <Route path="checkin" element={<CheckInDashboard />} />

              {/* Notifications */}
              <Route path="notifications" element={<NotificationsPage />} />

              {/* Accommodation - Admin Only */}
              <Route
                path="accommodation"
                element={
                  <RequireRole role={['admin']}>
                    <AccommodationPage />
                  </RequireRole>
                }
              />

              {/* Food Planning - Admin Only */}
              <Route
                path="food-planning"
                element={
                  <RequireRole role={['admin']}>
                    <FoodPlanningPage />
                  </RequireRole>
                }
              />

              {/* Settings - Admin Only */}
              <Route
                path="settings"
                element={
                  <RequireRole role={['admin']}>
                    <SettingsPage />
                  </RequireRole>
                }
              />

              {/* Data Maintenance - Admin Only */}
              <Route
                path="admin/data-maintenance"
                element={
                  <RequireRole role={['admin']}>
                    <AdminDataMaintenancePage />
                  </RequireRole>
                }
              />
            </Route>

            {/* Legacy routes redirect to new accommodation page */}
            <Route
              path="/dashboard/hotels"
              element={<Navigate to="/dashboard/accommodation" replace />}
            />
            <Route
              path="/dashboard/rooms"
              element={<Navigate to="/dashboard/accommodation" replace />}
            />
            <Route
              path="/dashboard/assignments"
              element={<Navigate to="/dashboard/accommodation" replace />}
            />
          </Routes>
        </Suspense>
      </AuthProvider>
    </HelmetProvider>
  )
}

export default App
